/**
 * Página admin — gestión de assets del onboarding (PDFs + URLs de
 * vídeo). Lista TODOS los slugs del catálogo estático
 * (`documentos.json` + `videos.json`), muestra cuál tiene fichero /
 * URL subido, y ofrece modal para subir o reemplazar.
 *
 * Auditor NO ve esta página — está bajo `AdminRoute` (user_type 1/2)
 * y filtrada en `CustomSider` vía `ADMIN_ONLY_RESOURCES`.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  App as AntdApp,
  Button,
  Card,
  Empty,
  Input,
  Modal,
  Popconfirm,
  Radio,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
} from "antd";
import {
  DeleteOutlined,
  FilePdfOutlined,
  InboxOutlined,
  LinkOutlined,
  ReloadOutlined,
  SearchOutlined,
  UploadOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";
import { PageHeader } from "@/components/common/PageHeader";
import { usePageTitle } from "@/hooks";
import { documents, videos } from "@/data/onboarding";
import type { DocumentEntry, VideoEntry } from "@/data/onboarding/types";
import type { OnboardingAsset } from "@/services/api/onboardingAssetService";
import { onboardingAssetService } from "@/services/api/onboardingAssetService";

const { Text } = Typography;

type Kind = "document" | "video";

interface Row {
  slug: string;
  title: string;
  kind: Kind;
  description?: string;
  asset: OnboardingAsset | null;
}

function formatSize(bytes: number | null | undefined): string {
  if (bytes == null) return "—";
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  const kb = bytes / 1024;
  return `${Math.round(kb)} KB`;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("es-ES", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

export const OnboardingAssetsAdminList: React.FC = () => {
  usePageTitle("Assets Onboarding — Admin");
  const { message } = AntdApp.useApp();

  const [assets, setAssets] = useState<OnboardingAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState<Row | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await onboardingAssetService.list();
      setAssets(list);
    } catch (e) {
      message.error(
        `No se pudieron cargar los assets: ${(e as Error).message}`,
      );
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    load();
  }, [load]);

  const rows: Row[] = useMemo(() => {
    const bySlug = new Map(assets.map((a) => [a.slug, a]));
    const docRows: Row[] = documents.map((d: DocumentEntry) => ({
      slug: d.slug,
      title: d.title,
      kind: "document",
      description: d.description,
      asset: bySlug.get(d.slug) ?? null,
    }));
    const videoRows: Row[] = videos.map((v: VideoEntry) => ({
      slug: v.slug,
      title: v.title,
      kind: "video",
      description: v.description,
      asset: bySlug.get(v.slug) ?? null,
    }));
    return [...docRows, ...videoRows];
  }, [assets]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.slug.toLowerCase().includes(q) ||
        r.title.toLowerCase().includes(q) ||
        (r.description ?? "").toLowerCase().includes(q),
    );
  }, [rows, query]);

  const pendingCount = rows.filter((r) => {
    if (r.kind === "document") return !r.asset?.url;
    return !r.asset?.video_url;
  }).length;

  const handleDelete = async (row: Row) => {
    if (!row.asset) return;
    try {
      await onboardingAssetService.remove(row.asset.id);
      message.success(`Asset "${row.slug}" eliminado.`);
      load();
    } catch (e) {
      message.error(`Error al eliminar: ${(e as Error).message}`);
    }
  };

  return (
    <>
      <PageHeader
        title="Assets del Onboarding"
        subtitle="Sube y actualiza los PDFs de los documentos y las URLs de los vídeos referenciados en el manual. Los slugs vienen del catálogo estático del repo — aquí sólo aportas el fichero o la URL."
      />

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Buscar por slug, título o descripción…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ minWidth: 320 }}
          />
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
            Refrescar
          </Button>
          <Text type="secondary">
            {filtered.length} de {rows.length} · {pendingCount} pendiente
            {pendingCount === 1 ? "" : "s"}
          </Text>
        </Space>
      </Card>

      {filtered.length === 0 ? (
        <Empty description="No hay entradas que coincidan." />
      ) : (
        <Table<Row>
          rowKey="slug"
          dataSource={filtered}
          loading={loading}
          pagination={false}
          size="small"
          columns={[
            {
              title: "Tipo",
              dataIndex: "kind",
              width: 90,
              render: (k: Kind) =>
                k === "document" ? (
                  <Tag icon={<FilePdfOutlined />} color="red">
                    PDF
                  </Tag>
                ) : (
                  <Tag icon={<VideoCameraOutlined />} color="purple">
                    Vídeo
                  </Tag>
                ),
            },
            {
              title: "Slug / Título",
              dataIndex: "title",
              render: (_, row) => (
                <div>
                  <div style={{ fontWeight: 600 }}>{row.title}</div>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    <code>{row.slug}</code>
                  </Text>
                </div>
              ),
            },
            {
              title: "Estado",
              width: 180,
              render: (_, row) => {
                if (row.kind === "document") {
                  if (row.asset?.url) {
                    return (
                      <Space direction="vertical" size={0}>
                        <Tag color="green">Subido</Tag>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {formatSize(row.asset.size_bytes)} ·{" "}
                          {formatDate(row.asset.uploaded_at)}
                        </Text>
                      </Space>
                    );
                  }
                  return <Tag>Pendiente</Tag>;
                }
                if (row.asset?.video_s3_key) {
                  return (
                    <Space direction="vertical" size={0}>
                      <Tag color="green">S3 privado</Tag>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {formatDate(row.asset.uploaded_at)}
                      </Text>
                    </Space>
                  );
                }
                if (row.asset?.video_url) {
                  return (
                    <Space direction="vertical" size={0}>
                      <Tag color="green">URL externa</Tag>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {formatDate(row.asset.uploaded_at)}
                      </Text>
                    </Space>
                  );
                }
                return <Tag>Pendiente</Tag>;
              },
            },
            {
              title: "Enlace",
              width: 100,
              render: (_, row) => {
                // Preferimos `asset.url` (firmada por el backend para
                // PDFs y vídeos privados en S3) sobre `video_url`
                // externo — cubre las 3 fuentes con una sola línea.
                const url = row.asset?.url ?? row.asset?.video_url ?? null;
                if (!url) return "—";
                return (
                  <Button
                    type="link"
                    icon={<LinkOutlined />}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                  >
                    Ver
                  </Button>
                );
              },
            },
            {
              title: "Acciones",
              width: 200,
              render: (_, row) => (
                <Space>
                  <Button
                    type="primary"
                    size="small"
                    icon={<UploadOutlined />}
                    onClick={() => setEditing(row)}
                  >
                    {row.asset ? "Reemplazar" : "Subir"}
                  </Button>
                  {row.asset ? (
                    <Popconfirm
                      title="¿Eliminar el asset?"
                      description="La entrada del catálogo seguirá viéndose como Pendiente hasta que subas otro."
                      okText="Eliminar"
                      okButtonProps={{ danger: true }}
                      cancelText="Cancelar"
                      onConfirm={() => handleDelete(row)}
                    >
                      <Button
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                      />
                    </Popconfirm>
                  ) : null}
                </Space>
              ),
            },
          ]}
        />
      )}

      {editing ? (
        <AssetUploadModal
          row={editing}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            load();
          }}
        />
      ) : null}
    </>
  );
};

/* ─── Modal de subida (PDF) o edición (URL vídeo) ────────────────── */

interface ModalProps {
  row: Row;
  onClose: () => void;
  onDone: () => void;
}

type VideoSource = "s3-private" | "external-url";

const AssetUploadModal: React.FC<ModalProps> = ({ row, onClose, onDone }) => {
  const { message } = AntdApp.useApp();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  // Inicializamos la fuente y sus inputs según el estado actual del asset.
  // Si el asset tiene una S3 key ya guardada → arrancamos en modo S3.
  // Si no, arrancamos en modo external-url.
  const initialSource: VideoSource = row.asset?.video_s3_key
    ? "s3-private"
    : "external-url";
  const [videoSource, setVideoSource] = useState<VideoSource>(initialSource);
  const [videoUrl, setVideoUrl] = useState<string>(row.asset?.video_url ?? "");
  const [videoS3Key, setVideoS3Key] = useState<string>(
    row.asset?.video_s3_key ?? "",
  );
  const [submitting, setSubmitting] = useState(false);

  const handleOk = async () => {
    setSubmitting(true);
    try {
      if (row.kind === "document") {
        const file = fileList[0]?.originFileObj;
        if (!file) {
          message.warning("Selecciona un PDF primero.");
          setSubmitting(false);
          return;
        }
        await onboardingAssetService.uploadDocument({
          slug: row.slug,
          title: row.title,
          file,
          existingId: row.asset?.id,
        });
        message.success("Documento subido.");
      } else if (videoSource === "s3-private") {
        // Ruta S3 privada — la firma la genera el backend al leer.
        let trimmed = videoS3Key.trim();
        if (!trimmed) {
          message.warning("Introduce la ruta S3 del vídeo.");
          setSubmitting(false);
          return;
        }
        // Aceptamos también `s3://<bucket>/onboarding_assets/xxx.mp4`
        // o `onboarding_assets/xxx.mp4` con prefijo — normalizamos a la
        // key relativa que espera el backend.
        trimmed = trimmed
          .replace(/^s3:\/\/[^/]+\//, "")
          .replace(/^onboarding_assets\//, "")
          .replace(/^\/+/, "");
        await onboardingAssetService.saveVideoS3Key({
          slug: row.slug,
          title: row.title,
          s3Key: trimmed,
          existingId: row.asset?.id,
        });
        message.success("Vídeo (S3 privado) guardado.");
      } else {
        const trimmed = videoUrl.trim();
        if (!trimmed) {
          message.warning("Introduce una URL de embed.");
          setSubmitting(false);
          return;
        }
        await onboardingAssetService.saveVideoUrl({
          slug: row.slug,
          title: row.title,
          videoUrl: trimmed,
          existingId: row.asset?.id,
        });
        message.success("URL de vídeo guardada.");
      }
      onDone();
    } catch (e) {
      message.error(`Error: ${(e as Error).message}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      title={`${row.asset ? "Reemplazar" : "Subir"} — ${row.title}`}
      onCancel={onClose}
      onOk={handleOk}
      confirmLoading={submitting}
      okText={row.kind === "document" ? "Subir" : "Guardar"}
      cancelText="Cancelar"
      width={560}
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Slug:
          </Text>{" "}
          <code>{row.slug}</code>
        </div>
        {row.kind === "document" ? (
          <Upload.Dragger
            multiple={false}
            accept="application/pdf"
            beforeUpload={() => false /* upload manual en handleOk */}
            fileList={fileList}
            onChange={({ fileList: fl }) => setFileList(fl.slice(-1))}
            onRemove={() => setFileList([])}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Arrastra un PDF aquí o haz click para elegir
            </p>
            <p className="ant-upload-hint">Un único fichero PDF.</p>
          </Upload.Dragger>
        ) : (
          <>
            <Text strong>Fuente del vídeo:</Text>
            <Radio.Group
              value={videoSource}
              onChange={(e) => setVideoSource(e.target.value as VideoSource)}
              optionType="button"
              buttonStyle="solid"
            >
              <Radio.Button value="s3-private">S3 privado (recomendado)</Radio.Button>
              <Radio.Button value="external-url">URL externa</Radio.Button>
            </Radio.Group>

            {videoSource === "s3-private" ? (
              <>
                <Text>Ruta S3 relativa a <code>onboarding_assets/</code>:</Text>
                <Input
                  placeholder="videos/procesado-muestras-hoja.mp4"
                  value={videoS3Key}
                  onChange={(e) => setVideoS3Key(e.target.value)}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Antes de rellenar aquí, sube el fichero manualmente con:
                  <br />
                  <code>
                    aws s3 cp mi-video.mp4 s3://cvp-prod-media/onboarding_assets/videos/mi-video.mp4 --content-type video/mp4
                  </code>
                  <br />
                  <strong>Sin <code>--acl public-read</code></strong> — el
                  fichero queda privado; el backend firma la URL con SigV4
                  cuando el auditor pulsa Play (expira en 1 h). Acepta
                  también pegar la URL completa (<code>s3://…</code> o
                  <code>onboarding_assets/…</code>) y la normaliza.
                </Text>
              </>
            ) : (
              <>
                <Text>URL de embed:</Text>
                <Input
                  placeholder="https://www.youtube.com/embed/XXXXXXXX"
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Para vídeos alojados en <strong>YouTube</strong> (unlisted),
                  <strong> Vimeo</strong> o <strong>CDN público</strong>.
                  Usa la URL de <strong>embed</strong>, p. ej.{" "}
                  <code>https://www.youtube.com/embed/&lt;VIDEO_ID&gt;</code>.
                </Text>
              </>
            )}
          </>
        )}
      </Space>
    </Modal>
  );
};
